// Sidebar.jsx
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import colors from "../constants/colors";

const menuItems = [
  { icon: "fas fa-tachometer-alt", text: "Dashboard", to: "/student/dashboard" },
  { icon: "fas fa-user", text: "Profile", to: "/student/profile" },
  { icon: "fas fa-chalkboard-teacher", text: "Courses", to: "/student/courses" },
  { icon: "fas fa-file-alt", text: "Exam Results", to: "/student/examresults" },
  { icon: "fas fa-wallet", text: "Fee Records", to: "/student/feemanagement" },
  { icon: "fas fa-question-circle", text: "Support", to: "/student/support" },

  // Logout item
  { icon: "fa-solid fa-right-from-bracket", text: "Logout", action: "logout" },
];

// Desktop Sidebar
const DesktopSidebar = () => {
  const location = useLocation();
  const [activeIndex, setActiveIndex] = useState(null);

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    window.location.href = "/login";
  };

  return (
    <aside
      className="w-56 min-h-screen sticky top-0 left-0 text-white shadow-2xl z-50"
      style={{
        background: colors.newGradientSidebar,
        boxShadow: `4px 0 15px ${colors.cardShadow}`,
      }}
    >
      <div className="p-6">
        <ul className="space-y-5">
          {menuItems.map((item, index) => {
            const isActive = location.pathname === item.to;

            // Logout button
            if (item.action === "logout") {
              return (
                <li key={index}>
                  <button
                    onClick={handleLogout}
                    className="flex w-full items-center space-x-3 p-2.5 rounded-md bg-white text-gray-800 hover:bg-red-100 hover:text-red-700 transition-all"
                  >
                    <i className={`${item.icon} text-lg`}></i>
                    <span className="font-semibold">{item.text}</span>
                  </button>
                </li>
              );
            }

            // Regular links
            return (
              <li key={index} className="rounded-md overflow-hidden">
                <Link
                  to={item.to}
                  onClick={() => setActiveIndex(index)}
                  className={`flex items-center space-x-3 p-2.5 rounded-md transition-all duration-300 cursor-pointer ${
                    isActive
                      ? "bg-gradient-to-r from-blue-800 to-blue-600 text-white shadow-md scale-[1.02]"
                      : "bg-white text-gray-800 hover:bg-blue-100 hover:text-blue-700 hover:shadow-md"
                  }`}
                >
                  <i className={`${item.icon} text-lg`}></i>
                  <span className="font-semibold">{item.text}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </aside>
  );
};

// Mobile Sidebar
const MobileSidebar = ({ isOpen, toggleSidebar }) => {
  const location = useLocation();

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    window.location.href = "/login";
  };

  return (
    <aside
      className={`fixed top-0 left-0 h-full w-64 text-white transform transition-transform duration-300 ease-in-out shadow-lg z-50 ${
        isOpen ? "translate-x-0" : "-translate-x-full"
      }`}
      style={{
        background: colors.newGradientSidebar,
        boxShadow: `4px 0 15px ${colors.cardShadow}`,
      }}
    >
      <div className="p-6">
        <ul className="space-y-5">
          {menuItems.map((item, index) => {
            // Logout button for mobile
            if (item.action === "logout") {
              return (
                <li key={index}>
                  <button
                    onClick={() => {
                      toggleSidebar();
                      handleLogout();
                    }}
                    className="flex w-full items-center space-x-4 p-3 hover:bg-red-600 transition-all text-white"
                  >
                    <i className={`${item.icon}`}></i>
                    <span className="text-lg font-medium">{item.text}</span>
                  </button>
                </li>
              );
            }

            // Normal links
            return (
              <li key={index} className="rounded-md overflow-hidden">
                <Link
                  to={item.to}
                  onClick={() => toggleSidebar()}
                  className={`flex items-center space-x-4 p-3 transition-all ${
                    location.pathname === item.to
                      ? "bg-gradient-to-r from-blue-800 to-blue-600 text-white shadow-md"
                      : "hover:bg-blue-700"
                  }`}
                >
                  <i className={`${item.icon} text-gray-100`}></i>
                  <span className="text-lg font-medium">{item.text}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </aside>
  );
};

// Main Component
const Sidebar = ({ isOpen, toggleSidebar }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isMobile && isOpen) toggleSidebar();
  }, [isMobile]);

  return isMobile ? (
    <MobileSidebar isOpen={isOpen} toggleSidebar={toggleSidebar} />
  ) : (
    <DesktopSidebar />
  );
};

export default Sidebar;
